/**
 * A vendor cabinet part in words (2026-10-15): what it is and its size, the
 * way a contractor reads a price sheet. The Price Book's vendor card lists a
 * part so, and an estimate names its line so — with the part number and the
 * colour a contractor orders by — where "B12 — Hall Mark Frost" told a
 * homeowner nothing. Kinds are the vendor catalogs' (`vendors/<line>/
 * catalog.json`). Pure.
 */
const { familySizes } = require('./vendorSizes');

const inches = (n) => `${n}"`;

// Venus's counted trim pieces, by part number (catalog pp. 22–25)
const TRIM_MOLDING_NAMES = {
  'SM8-S': 'Scribe molding',
  QRM: 'Shoe molding',
  ICM8: 'Inside corner molding',
  OCM8: 'Outside corner molding',
  'BM1-1/4': 'Batten molding',
  RM: 'Rope molding',
  DM: 'Dentil molding',
};

// The counted pieces sold each (2026-10-31, group 3a), named as the panel
// lists them; their size follows (`vendorProfile`).
const COUNTED_PIECE_NAMES = {
  CMCB: 'Corbel',
  CML: 'Corbel',
  SP336: 'Square post',
  RP336: 'Round post',
  RP436: 'Round post',
  CP36: 'Corner post',
  VST96: 'Straight valance',
};

/** What a unit is, the way a contractor reads a price sheet. */
const DESCRIBE = {
  base: (u) => `Base ${inches(u.w)}`,
  'base-full-door': (u) => `Base, full door ${inches(u.w)}`,
  'drawer-base': (u) => `Drawer base ${inches(u.w)}`,
  'two-drawer-base': (u) => `Two-drawer base ${inches(u.w)}`,
  'sink-base': (u) => `Sink base ${inches(u.w)}`,
  'farm-sink-base': (u) => `Farm sink base ${inches(u.w)}`,
  'diagonal-sink-base': (u) => `Diagonal sink base ${inches(u.w)}`,
  'corner-blind-base': (u) =>
    `Blind corner base ${inches(u.w)}–${inches(u.wMax ?? u.w)}`,
  'lazy-susan': (u) => `Lazy susan ${inches(u.w)}`,
  wall: (u) => `Wall ${inches(u.w)} × ${inches(u.h)}`,
  'wall-bridge-24': (u) =>
    `Over-fridge cabinet ${inches(u.w)} × ${inches(u.h)}, ${inches(u.d)} deep`,
  'wall-blind': (u) => `Blind corner wall ${inches(u.w)} × ${inches(u.h)}`,
  'wall-diagonal': (u) =>
    `Diagonal corner wall ${inches(u.w)} × ${inches(u.h)}`,
  'wall-pie': (u) => `Pie corner wall ${inches(u.w)} × ${inches(u.h)}`,
  'tall-pantry': (u) => `Tall pantry ${inches(u.w)} × ${inches(u.h)}`,
  oven: (u) => `Oven cabinet ${inches(u.w)} × ${inches(u.h)}`,
  'vanity-base': (u) => `Vanity base ${inches(u.w)}`,
  'vanity-drawer-base': (u) => `Vanity drawer base ${inches(u.w)}`,
  'vanity-sink-base': (u) => `Vanity sink base ${inches(u.w)}`,
  'vanity-sink-drawer': (u) =>
    `Vanity sink drawer ${inches(u.w)}${u.hand ? ` (${u.hand})` : ''}${
      u.bowls === 2 ? ', double' : ''
    }`,
  'knee-drawer': (u) => `Knee drawer ${inches(u.w)}`,
  'fridge-end-panel': (u) => `Refrigerator end panel ${inches(u.h)} tall`,
  'base-filler': (u) => `Base filler ${inches(u.w)}`,
  'wall-filler': (u) => `Wall filler ${inches(u.w)} × ${inches(u.h)}`,
  'dishwasher-return': (u) => `Dishwasher return, ${inches(u.w)} filler`,
  'back-panel': (u) =>
    `Back panel ${inches(u.h)} × ${inches(u.w)}, ${inches(u.d)} thick`,
  'base-side-panel': (u) => `Side panel ${inches(u.d)} × ${inches(u.h)}`,
  'vanity-side-panel': (u) =>
    `Vanity side panel ${inches(u.d)} × ${inches(u.h)}`,
  'toe-kick': (u) => `Toe kick ${inches(u.w)}`,
  // the door a glass upper's box is sold with (2026-10-21, plan C10)
  'glass-door': (u) => `Glass door ${inches(u.w)} × ${inches(u.h)}`,
  // a tray fitted inside a base of its width (2026-10-25)
  'rollout-tray': (u) => `Roll-out tray ${inches(u.w)}`,
  // cabinet trim, 96" sticks (2026-10-30, `shared-lib/cabinet-trim`)
  'crown-molding': (u) => `Crown molding ${inches(u.h)}`,
  'stacking-molding': (u) => `Stacking molding ${inches(u.h)}`,
  'light-rail': (u) => `Light rail ${inches(u.h)}`,
  'trim-molding': (u) => TRIM_MOLDING_NAMES[u.sku] || u.sku,
  // a wood box hood over a Venus run's range (2026-10-31, group 2)
  'wood-hood': (u) => `Wood range hood ${inches(u.w)} × ${inches(u.h)}`,
  corbel: (u) => `Corbel ${inches(u.w)} × ${inches(u.h)}`,
  'decorative-post': (u) =>
    `${COUNTED_PIECE_NAMES[u.sku] || 'Post'} ${inches(u.h)}`,
  'valance-board': (u) => `Straight valance ${inches(u.w)} × ${inches(u.h)}`,
  // a floating shelf board (group 3b)
  'floating-shelf': (u) => `Floating shelf ${inches(u.w)}`,
  // the valance over the sink (group 3c), by its style
  valance: (u) =>
    `${u.style === 'raised-panel' ? 'Raised-panel' : 'Arched'} valance ${inches(u.w)} × ${inches(u.h)}`,
  // the door-style panel on an exposed cabinet side (group 3d)
  'end-panel': (u) => `Wainscot end panel ${inches(u.w)} × ${inches(u.h)}`,
  // end shelves (group 3e), by the side a base one stands open on
  'base-end-shelf': (u) =>
    `Base end shelf ${inches(u.w)}, ${u.open === 'left' ? 'left' : 'right'} open`,
  'wall-end-shelf': (u) => `Wall end shelf ${inches(u.w)} × ${inches(u.h)}`,
  // the special cabinets (group 4)
  'trash-base': (u) => `Trash pull-out base ${inches(u.w)}`,
  'spice-base': (u) => `Spice pull-out base ${inches(u.w)}`,
  'base-microwave': (u) => `Microwave base ${inches(u.w)}`,
  'wall-microwave': (u) =>
    `Microwave wall cabinet ${inches(u.w)} × ${inches(u.h)}`,
  // the racks (group 5)
  'wine-rack': (u) => `Lattice wine rack ${inches(u.w)} × ${inches(u.h)}`,
  'wine-x-rack': (u) => `Wine rack with an X ${inches(u.w)} × ${inches(u.h)}`,
  'plate-rack': (u) => `Plate rack ${inches(u.w)} × ${inches(u.h)}`,
  'wine-cubes': (u) => `Wine cubes ${inches(u.w)} × ${inches(u.h)}`,
  'glass-holder': (u) => `Glass holder ${inches(u.w)}`,
  // the angled ends (group 6)
  'base-angled-end': (u) => `Angled end base ${inches(u.w)}`,
  'wall-angled-end': (u) =>
    `Angled end wall cabinet ${inches(u.w)} × ${inches(u.h)}`,
};

/** A catalog unit in words; its part number for a kind with none. */
const describeVendorUnit = (u) => (DESCRIBE[u.kind] || ((x) => x.sku))(u);

/**
 * An estimate line's name for one of a line's parts in a colour:
 * `Base 12" (B12) — Hall Mark Frost`, or `B12 — …` for a part the line's
 * catalog does not list.
 */
function vendorUnitName(family, sku, colourName) {
  return `${vendorPartName(family, sku)} — ${colourName}`;
}

/** A part with no colour, in words: `Roll-out tray 24" (RD24)`. */
function vendorPartName(family, sku) {
  const unit = familySizes(family)?.catalog?.units?.find((u) => u.sku === sku);
  return unit ? `${describeVendorUnit(unit)} (${sku})` : sku;
}

module.exports = {
  COUNTED_PIECE_NAMES,
  describeVendorUnit,
  vendorUnitName,
  vendorPartName,
  TRIM_MOLDING_NAMES,
};
